import React from 'react';
import {
  Routes,
  Route,
  useLocation,
  useSearchParams,
  Navigate,
} from 'react-router-dom';
import ForgotPassword from '../pages/Auth/ForgotPassword';
import MagicLink from '../pages/Auth/MagicLink';
import Home from '../pages/Home';
import Journeys from '../pages/Videos/Journeys';
import Profile from '../pages/Profile';
import Projects from '../pages/Projects';
import Lessons from '../pages/Videos/Lessons';
import PrivacyPolicy from '../pages/Auth/PrivacyPolicy';
import DataDeletion from '../pages/Auth/DataDeletion';
import PrivateRoute, {
  GuestRoute,
  ProfileCompletionRoute,
} from './PrivateRoute';
import CompleteProfile from '../pages/Auth/CompleteProfile';
import NotFound from '../pages/Errors/NotFound';
import Tutorial from '../pages/Projects/Tutorial';
import SignIn from '../pages/Auth/SignIn';
import SignUp from '../pages/Auth/SignUp';
import PrivacyPolicyModal from '../modals/PrivacyPolicyModal';
import Trails from '../pages/Projects/Trails';
import HardwarePage from '../pages/Hardwares/HardwarePage';
import HardwareProfile from '../pages/Hardwares/HardwareProfile';
import PlaylistsAndLessons from '../pages/Videos/PlaylistsAndLessons';
import MakeCodePage from '../pages/Projects/MakeCodePage';
import ChaptersAndProgress from '../pages/Projects/ChaptersAndProgress';
import CommunityPage from '../pages/Community/CommunityPage';
import CreatePost from '../pages/Community/CreatePost';
import EditPost from '../pages/Community/EditPost';
import ChallengePage from '../pages/Challenges/ChallengePage';
import ProfileCertificates from '../pages/Profile/ProfileCertificates';
import ProfileBadges from '../pages/Profile/ProfileBadges';
import ProfileNotifications from '../pages/Profile/ProfileNotifications';
import PersonalDataForm from '../pages/Profile/PersonalDataForm';
import Certificate from '../pages/Certificate';
import RankingPage from '../pages/RankingPage';
import CommunityPost from '../modals/CommunityPostModal';
import ListTournaments from '../pages/Tournaments/ListTournaments';
import CreateTournament from '../pages/Tournament/CreateTournament';
import TournamentDetails from '../pages/Tournament/TournamentDetails';
import TournamentTimeline from '../pages/Tournament/TournamentTimeline';
import TournamentTeams from '../pages/Tournament/TournamentTeams';
import TournamentRoundEvaluation from '../pages/Tournament/TournamentRoundEvaluation';
import TournamentPresentations from '../pages/Tournament/TournamentPresentations';
import TournamentBoard from '../pages/TournamentBoard';
import TournamentBoardDemo from '../pages/TournamentBoard/demo';

const Routex: React.FC = () => {
  const location = useLocation();
  const background = location.state as { backgroundLocation?: Location };
  const [searchParams] = useSearchParams();

  const isModal = searchParams.get('modal') === 'true';

  return (
    <Routes location={background?.backgroundLocation || location}>
      {/* link para validação de certificados */}
      <Route path="certificates/validate/:id" element={<Certificate />} />
      {/* página pública de exclusão de dados (LGPD, art. 18, VI) */}
      <Route path="data-deletion" element={<DataDeletion />} />
      <Route path="/" element={<GuestRoute component={SignIn} />} />
      <Route path="signup" element={<GuestRoute component={SignUp} />} />
      <Route
        path="forgot-password"
        element={<GuestRoute component={ForgotPassword} />}
      />
      <Route path="magic-link" element={<MagicLink />} />
      <Route
        path="complete-profile"
        element={<ProfileCompletionRoute component={CompleteProfile} />}
      />
      <Route
        path="home"
        element={<PrivateRoute isPrivate component={Home} />}
      />
      <Route
        path="profile"
        element={<PrivateRoute isPrivate component={Profile} />}
      >
        <Route index element={<Navigate to="personal-data" replace />} />
        <Route path="personal-data" element={<PersonalDataForm />} />
        <Route path="certificates" element={<ProfileCertificates />} />
        <Route path="achievements" element={<ProfileBadges />} />
        <Route path="notifications" element={<ProfileNotifications />} />
      </Route>

      <Route
        path="hardwares"
        element={<PrivateRoute isPrivate component={HardwarePage} />}
      />

      <Route
        path="hardwares/profile/:hardware"
        element={<PrivateRoute isPrivate component={HardwareProfile} />}
      />

      <Route
        path="videos/:active_tab"
        element={<PrivateRoute isPrivate component={Journeys} />}
      />

      <Route
        path="videos/:journey_slug/playlists"
        element={<PrivateRoute isPrivate component={PlaylistsAndLessons} />}
      />

      <Route
        path="videos/:journey_slug/playlists/:playlist_slug"
        element={<PrivateRoute isPrivate component={Lessons} />}
      />

      <Route
        path="videos/:journey_slug/playlists/:playlist_slug/lessons/:lesson_slug"
        element={<PrivateRoute isPrivate component={Lessons} />}
      />

      {/* TRAILS */}
      <Route
        path="projects/:active_tab"
        element={<PrivateRoute isPrivate component={Projects} />}
      />

      <Route
        path="trails/:hardware_slug"
        element={<PrivateRoute isPrivate component={Trails} />}
      />

      <Route
        path="/makecode"
        element={<PrivateRoute isPrivate component={MakeCodePage} />}
      />
      <Route
        path="trails/:trail_slug/chapters"
        element={<PrivateRoute isPrivate component={ChaptersAndProgress} />}
      />

      <Route
        path="trails/:trail_slug/chapters/:chapter_slug"
        element={<PrivateRoute isPrivate component={ChaptersAndProgress} />}
      />

      <Route
        path="projects/:trail_slug/:chapter_id/activity/:activity_slug/"
        element={<PrivateRoute isPrivate component={Tutorial} />}
      />

      <Route
        path="/privacypolicy"
        element={<PrivateRoute isPrivate component={PrivacyPolicy} />}
      />

      <Route
        path="/community"
        element={<PrivateRoute isPrivate component={CommunityPage} />}
      >
        <Route path="post/:project_id" element={<CommunityPost />} />
      </Route>

      <Route
        path="/create/post"
        element={<PrivateRoute isPrivate component={CreatePost} />}
      />

      <Route
        path="/create/post/challenge/:challengeSlug"
        element={<PrivateRoute isPrivate component={CreatePost} />}
      />

      <Route
        path="/edit/post/:postId"
        element={<PrivateRoute isPrivate component={EditPost} />}
      />

      <Route
        path="/challenges"
        element={<PrivateRoute isPrivate component={ChallengePage} />}
      >
        {isModal && (
          <Route path="modal/details" element={<PrivacyPolicyModal />} />
        )}
      </Route>

      <Route
        path="/ranking"
        element={<PrivateRoute isPrivate component={RankingPage} />}
      />

      <Route
        path="/tournaments"
        element={<PrivateRoute isPrivate component={ListTournaments} />}
      />

      <Route
        path="/tournament/create"
        element={<PrivateRoute isPrivate component={CreateTournament} />}
      />

      <Route
        path="/tournament/:id"
        element={<PrivateRoute isPrivate component={TournamentDetails} />}
      />

      <Route
        path="/tournament/:id/schedule/edit"
        element={<PrivateRoute isPrivate component={TournamentTimeline} />}
      />

      <Route
        path="/tournament/:id/teams"
        element={<PrivateRoute isPrivate component={TournamentTeams} />}
      />

      <Route
        path="/tournament/:id/presentations"
        element={<PrivateRoute isPrivate component={TournamentPresentations} />}
      />

      <Route
        path="/tournament/:id/rounds/:round_id"
        element={
          <PrivateRoute isPrivate component={TournamentRoundEvaluation} />
        }
      />
      {/* <Route
        path="/upload"
        element={<PrivateRoute isPrivate component={UploadProject} />}
      >
        <Route path="*" element={<CreateProject />} />
        <Route path="create" element={<CreateProject />} />
        <Route path="modify" element={<ModifyProject />} />
      </Route> */}

      {/* <Route
        path="/preview/:trail_slug/:chapter_id/activity/:activity_slug/"
        element={<PrivateRoute isPrivate component={PreviewProject} />}
      /> */}

      {/* Sem isto, uma URL desconhecida renderiza a página em branco. */}
      {/* Telão público: sem login e sem o layout do app (QR code / TV). */}
      <Route path="/telao/demo" element={<TournamentBoardDemo />} />
      <Route path="/telao/:token" element={<TournamentBoard />} />

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default Routex;
